import type { Reducer } from 'redux';

import { getGraphqlApiErrorReason } from '../../..';
import type { UpdateHomeFeedPreferenceAction } from '../../../domains/sessionHomeFeedPreference/actions.types';
import type {
  SessionLoadCurrentUserAction,
  SessionLoadCurrentUserFulfilledAction,
  SessionLoadCurrentUserRejectedAction,
} from '../../../domains/sessionLoadCurrentUser/actions.types';
import type { UserInformationBarrierSettingsLoadFulfilledAction } from '../../../domains/userInformationBarrierSettings/actions.types';
import type {
  UpdateUserPreferencesAction,
  UpdateUserPreferencesFulfilledAction,
} from '../../../domains/userPreferences/actions.types';

import type { CurrentUser } from './types';
export type UserAction =
  | SessionLoadCurrentUserAction
  | UpdateUserPreferencesAction
  | UpdateHomeFeedPreferenceAction
  | UserInformationBarrierSettingsLoadFulfilledAction;
const initialState: CurrentUser = {
  id: '',
  hasLicense: true,
  isGuest: true,
  isPending: false,
  isNetworkAdmin: false,
  isVerifiedNetworkAdmin: false,
  isRestrictedToViewOnlyMode: false,
  userPreferences: { webClientPreference: 'NONE' },
  isUserForceOptedIn: false,
  homeFeedPreference: 'DISCOVERY',
  canScheduleAndCreateTeamsMeeting: false,
  canSendOnBehalfOf: false,
  directMessageThreadStarterSmallFileUploadUrl: '',
  shouldShowInformationBarrierPublisherWarning: false,
};
const checkForLicenseError = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  if (getGraphqlApiErrorReason(action.payload.error) === 'NO_YAMMER_LICENSE') {
    return { ...state, hasLicense: false };
  }

  return state;
};
const checkForPendingUserError = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  if (getGraphqlApiErrorReason(action.payload.error) === 'PENDING_USER') {
    return { ...state, isPending: true };
  }

  return state;
};
const checkForErrors = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  const { error } = action.payload;

  return { ...state, error };
};
const stateWithAdmins = (state: CurrentUser, action: SessionLoadCurrentUserFulfilledAction) => {
  const {
    isNetworkAdmin,
    isGuest,
    isVerifiedNetworkAdmin,
    isRestrictedToViewOnlyMode,
    id,
    userPreferences,
    isUserForceOptedIn,
    canScheduleAndCreateTeamsMeeting,
    canSendOnBehalfOf,
    directMessageThreadStarterSmallFileUploadUrl,
  } = action.payload;

  return {
    ...state,
    id,
    isGuest,
    isNetworkAdmin,
    isVerifiedNetworkAdmin,
    isRestrictedToViewOnlyMode,
    userPreferences,
    isUserForceOptedIn,
    canScheduleAndCreateTeamsMeeting,
    canSendOnBehalfOf,
    directMessageThreadStarterSmallFileUploadUrl,
  };
};
const stateWithUpdatedUserPreferences = (state: CurrentUser, action: UpdateUserPreferencesFulfilledAction) => ({
  ...state,
  userPreferences: {
    ...state.userPreferences,
    webClientPreference: action.payload.preference,
  },
});
const stateWithUpdatedPublisherWarning = (
  state: CurrentUser,
  action: UserInformationBarrierSettingsLoadFulfilledAction
) => ({
  ...state,
  shouldShowInformationBarrierPublisherWarning: action.payload.shouldShowInformationBarrierPublisherWarning,
});
const stateWithUpdatedHomeFeedPreference = (state: CurrentUser, action: UpdateHomeFeedPreferenceAction) => ({
  ...state,
  ...action.payload,
});
const reducer: Reducer<CurrentUser, UserAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'SESSION.LOAD_CURRENT_USER_FULFILLED':
      return stateWithAdmins(state, action);
    case 'SESSION.LOAD_CURRENT_USER_REJECTED': {
      const stateWithLicense = checkForLicenseError(state, action);
      const statewithPendingUser = checkForPendingUserError(stateWithLicense, action);

      return checkForErrors(statewithPendingUser, action);
    }
    case 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_FULFILLED':
      return stateWithUpdatedUserPreferences(state, action);
    case 'USER_INFORMATION_BARRIER_SETTINGS.LOAD_FULFILLED':
      return stateWithUpdatedPublisherWarning(state, action);
    case 'SESSION.UPDATE_HOME_FEED_PREFERENCE':
      return stateWithUpdatedHomeFeedPreference(state, action);
    default:
      return state;
  }
};
export default reducer;
